import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { qnaApi, type QnaInput } from '../api/client'
import { useAuth } from '../context/AuthContext'
import { QNA_CATEGORY_OPTIONS, type QnaCategory, type QnaEntry } from '../types'

const SEARCH_DEBOUNCE_MS = 300

function highlight(text: string, query: string) {
  if (!query.trim()) return text
  const idx = text.toLowerCase().indexOf(query.toLowerCase())
  if (idx === -1) return text
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-amber-200 text-inherit rounded-sm">{text.slice(idx, idx + query.length)}</mark>
      {text.slice(idx + query.length)}
    </>
  )
}

function QnaForm({ initial, onCancel, onSubmit, submitting }: {
  initial?: QnaEntry
  onCancel: () => void
  onSubmit: (input: QnaInput) => void
  submitting: boolean
}) {
  const { t } = useTranslation()
  const [question, setQuestion] = useState(initial?.question ?? '')
  const [answer, setAnswer] = useState(initial?.answer ?? '')
  const [questionEn, setQuestionEn] = useState(initial?.questionEn ?? '')
  const [answerEn, setAnswerEn] = useState(initial?.answerEn ?? '')
  const [category, setCategory] = useState<QnaCategory>(initial?.category ?? 'SERVICE')
  const valid = question.trim().length > 0 && answer.trim().length > 0

  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-3">
      <div>
        <label className="block text-[11px] font-semibold text-slate-600 mb-1">{t('qna.form.category')}</label>
        <select value={category} onChange={e => setCategory(e.target.value as QnaCategory)}
          disabled={submitting} className="input-field">
          {QNA_CATEGORY_OPTIONS.map(o => (
            <option key={o.value} value={o.value}>{t(o.labelKey)}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-[11px] font-semibold text-slate-600 mb-1">{t('qna.form.question')}</label>
        <input value={question} onChange={e => setQuestion(e.target.value)} disabled={submitting} className="input-field" />
      </div>
      <div>
        <label className="block text-[11px] font-semibold text-slate-600 mb-1">{t('qna.form.answer')}</label>
        <textarea value={answer} onChange={e => setAnswer(e.target.value)} rows={4} disabled={submitting} className="input-field resize-none" />
      </div>
      <div>
        <label className="block text-[11px] font-semibold text-slate-600 mb-1">{t('qna.form.questionEn')}</label>
        <input value={questionEn} onChange={e => setQuestionEn(e.target.value)} disabled={submitting} className="input-field" />
      </div>
      <div>
        <label className="block text-[11px] font-semibold text-slate-600 mb-1">{t('qna.form.answerEn')}</label>
        <textarea value={answerEn} onChange={e => setAnswerEn(e.target.value)} rows={4} disabled={submitting} className="input-field resize-none" />
        <p className="text-[11px] text-slate-400 mt-1">{t('qna.form.englishHint')}</p>
      </div>
      <div className="flex justify-end gap-2">
        <button onClick={onCancel} disabled={submitting} className="btn-secondary disabled:opacity-50">{t('common.cancel')}</button>
        <button
          onClick={() => valid && onSubmit({ question: question.trim(), answer: answer.trim(), questionEn: questionEn.trim(), answerEn: answerEn.trim(), category })}
          disabled={!valid || submitting}
          className="px-4 py-1.5 rounded-md text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 transition-colors disabled:opacity-50"
        >
          {submitting ? t('common.saving') : t('common.save')}
        </button>
      </div>
    </div>
  )
}

export default function QnaPage() {
  const { t, i18n } = useTranslation()
  const isEn = (i18n.resolvedLanguage ?? i18n.language)?.toLowerCase().startsWith('en')
  // 영어 화면에서는 영어 번역이 있으면 그것을, 없으면 한국어 본문을 보여준다
  const localized = useCallback(
    (e: QnaEntry) => ({
      question: isEn && e.questionEn ? e.questionEn : e.question,
      answer: isEn && e.answerEn ? e.answerEn : e.answer,
    }),
    [isEn],
  )
  const navigate = useNavigate()
  const { user } = useAuth()
  const isAdmin = !!user?.admin

  const [entries, setEntries] = useState<QnaEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set())
  const [creating, setCreating] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const [searchInput, setSearchInput] = useState('')
  const [query, setQuery] = useState('')
  const [activeCategory, setActiveCategory] = useState<QnaCategory | 'ALL'>('ALL')
  const [copiedId, setCopiedId] = useState<number | null>(null)
  const [feedback, setFeedback] = useState<Record<number, 'up' | 'down'>>({})
  const hashHandled = useRef(false)

  useEffect(() => {
    const timer = setTimeout(() => setQuery(searchInput.trim()), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [searchInput])

  const fetchList = async () => {
    setLoading(true)
    try {
      const data = await qnaApi.list()
      setEntries(data)
    } catch {
      setError(t('qna.loadFailed'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchList() }, [])

  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {}
    entries.forEach(e => { counts[e.category] = (counts[e.category] ?? 0) + 1 })
    return counts
  }, [entries])

  const filtered = useMemo(() => {
    const q = query.toLowerCase()
    return entries.filter(e => {
      if (activeCategory !== 'ALL' && e.category !== activeCategory) return false
      if (!q) return true
      const { question, answer } = localized(e)
      return question.toLowerCase().includes(q) || answer.toLowerCase().includes(q)
    })
  }, [entries, query, activeCategory, localized])

  // 검색 중일 때는 일치하는 항목을 자동으로 펼쳐 클릭 한 번을 줄여준다
  useEffect(() => {
    if (query) {
      setExpandedIds(new Set(filtered.map(e => e.id)))
    } else {
      setExpandedIds(new Set())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query])

  // 진입 시 URL 해시(#qna-123)가 있으면 해당 항목을 펼치고 스크롤
  useEffect(() => {
    if (hashHandled.current || loading || entries.length === 0) return
    const hash = window.location.hash.replace('#', '')
    const match = hash.match(/^qna-(\d+)$/)
    if (match) {
      const id = Number(match[1])
      setExpandedIds(prev => new Set(prev).add(id))
      setTimeout(() => {
        document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }, 100)
    }
    hashHandled.current = true
  }, [loading, entries])

  const toggle = (id: number) => {
    setExpandedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const copyLink = async (id: number) => {
    const url = `${window.location.origin}/qna#qna-${id}`
    try {
      await navigator.clipboard.writeText(url)
      setCopiedId(id)
      setTimeout(() => setCopiedId(null), 1500)
    } catch {
      // 클립보드 접근이 차단된 환경 — 조용히 무시
    }
  }

  const handleCreate = async (input: QnaInput) => {
    setSubmitting(true)
    try {
      await qnaApi.create(input)
      setCreating(false)
      fetchList()
    } catch {
      setError(t('qna.createFailed'))
    } finally {
      setSubmitting(false)
    }
  }

  const handleUpdate = async (id: number, input: QnaInput) => {
    setSubmitting(true)
    try {
      await qnaApi.update(id, input)
      setEditingId(null)
      fetchList()
    } catch {
      setError(t('qna.updateFailed'))
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async (id: number) => {
    try {
      await qnaApi.delete(id)
      fetchList()
    } catch {
      setError(t('qna.deleteFailed'))
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="h-12 bg-slate-900 flex items-center gap-3 px-4 shrink-0">
        <button
          onClick={() => navigate('/dashboard')}
          className="text-xs text-slate-400 hover:text-white border border-slate-600 hover:border-slate-400 rounded px-3 py-1 transition-colors flex items-center gap-1.5"
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          {t('support.back')}
        </button>
        <h1 className="text-white text-sm font-semibold ml-1">{t('qna.pageTitle')}</h1>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-10 space-y-4">
        <div>
          <p className="text-lg font-semibold text-slate-800">{t('qna.heading')}</p>
          <p className="text-sm text-slate-500 mt-0.5">{t('qna.subtitle')}</p>
        </div>

        {error && <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2.5 rounded text-sm">{error}</div>}

        <div className="relative">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            placeholder={t('qna.searchPlaceholder')}
            className="input-field pl-9"
          />
        </div>

        <div className="flex flex-wrap gap-1.5">
          <button
            onClick={() => setActiveCategory('ALL')}
            className={activeCategory === 'ALL'
              ? 'text-xs font-semibold px-3.5 py-1.5 rounded-full bg-primary-600 text-white'
              : 'text-xs px-3.5 py-1.5 rounded-full bg-white border border-slate-200 text-slate-600 hover:border-slate-300'}
          >
            {t('qna.all')} <span className="opacity-75">{entries.length}</span>
          </button>
          {QNA_CATEGORY_OPTIONS.map(o => (
            <button
              key={o.value}
              onClick={() => setActiveCategory(o.value)}
              className={activeCategory === o.value
                ? 'text-xs font-semibold px-3.5 py-1.5 rounded-full bg-primary-600 text-white'
                : 'text-xs px-3.5 py-1.5 rounded-full bg-white border border-slate-200 text-slate-600 hover:border-slate-300'}
            >
              {t(o.labelKey)} <span className={activeCategory === o.value ? 'opacity-75' : 'text-slate-400'}>{categoryCounts[o.value] ?? 0}</span>
            </button>
          ))}
        </div>

        <div className="flex items-center justify-between">
          <p className="text-xs text-slate-400">{t('qna.count', { count: filtered.length })}</p>
          {isAdmin && !creating && (
            <button onClick={() => setCreating(true)} className="text-xs text-primary-600 font-semibold hover:underline">
              {t('qna.addEntry')}
            </button>
          )}
        </div>

        {creating && (
          <QnaForm onCancel={() => setCreating(false)} onSubmit={handleCreate} submitting={submitting} />
        )}

        {loading ? (
          <div className="text-center text-sm text-slate-400 py-10">{t('common.loading')}</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-10 space-y-2">
            <p className="text-sm text-slate-500">
              {query ? t('qna.noResultsFor', { query }) : t('qna.noEntries')}
            </p>
            {query && <p className="text-xs text-slate-400">{t('qna.tryOther')}</p>}
            <button
              onClick={() => navigate('/contact')}
              className="mt-2 px-4 py-2 rounded-md text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 transition-colors"
            >
              {t('qna.contactCta')}
            </button>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden divide-y divide-slate-100">
            {filtered.map(entry => {
              const expanded = expandedIds.has(entry.id)
              return (
                <div key={entry.id} id={`qna-${entry.id}`}>
                  {editingId === entry.id ? (
                    <div className="p-3">
                      <QnaForm
                        initial={entry}
                        onCancel={() => setEditingId(null)}
                        onSubmit={(input) => handleUpdate(entry.id, input)}
                        submitting={submitting}
                      />
                    </div>
                  ) : (
                    <>
                      <button
                        onClick={() => toggle(entry.id)}
                        className="w-full flex items-start gap-2.5 px-4 py-3 text-left hover:bg-slate-50"
                      >
                        <span className="text-[10px] font-bold text-primary-700 bg-primary-50 px-2 py-0.5 rounded shrink-0 mt-0.5">{t(`qna.category.${entry.category}`)}</span>
                        <span className="text-sm font-semibold text-slate-800 flex-1">{highlight(localized(entry).question, query)}</span>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                          className={`text-slate-400 shrink-0 mt-0.5 transition-transform ${expanded ? 'rotate-180' : ''}`}>
                          <polyline points="6 9 12 15 18 9" />
                        </svg>
                      </button>
                      {expanded && (
                        <div className="px-4 pb-4 pl-[4.5rem] -mt-1 space-y-3">
                          <p className="text-sm text-slate-600 whitespace-pre-wrap leading-relaxed">{highlight(localized(entry).answer, query)}</p>

                          <div className="flex items-center gap-3 flex-wrap text-xs">
                            <button onClick={() => navigate('/contact')} className="text-primary-600 font-medium hover:underline">
                              {t('qna.contactArrow')}
                            </button>
                            <span className="flex-1" />
                            {!feedback[entry.id] ? (
                              <>
                                <span className="text-slate-400">{t('qna.helpful')}</span>
                                <button onClick={() => setFeedback(f => ({ ...f, [entry.id]: 'up' }))}
                                  className="px-2 py-1 border border-slate-200 rounded text-slate-500 hover:border-slate-300">{t('qna.yes')}</button>
                                <button onClick={() => setFeedback(f => ({ ...f, [entry.id]: 'down' }))}
                                  className="px-2 py-1 border border-slate-200 rounded text-slate-500 hover:border-slate-300">{t('qna.no')}</button>
                              </>
                            ) : (
                              <span className="text-slate-400">{t('qna.thanks')}</span>
                            )}
                            <button onClick={() => copyLink(entry.id)} className="text-slate-400 hover:text-slate-600">
                              {copiedId === entry.id ? t('qna.linkCopied') : t('qna.copyLink')}
                            </button>
                          </div>

                          {isAdmin && (
                            <div className="flex gap-3 pt-1 border-t border-slate-100">
                              <button onClick={() => setEditingId(entry.id)} className="text-[11px] text-slate-500 hover:text-primary-600 mt-2">{t('common.edit')}</button>
                              <button onClick={() => handleDelete(entry.id)} className="text-[11px] text-slate-500 hover:text-red-500 mt-2">{t('common.delete')}</button>
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )
            })}
          </div>
        )}

        <div className="flex items-center justify-between gap-3 bg-white border border-slate-200 rounded-lg px-4 py-3.5 flex-wrap">
          <div>
            <p className="text-sm font-semibold text-slate-800">{t('qna.notFoundTitle')}</p>
            <p className="text-xs text-slate-500 mt-0.5">{t('qna.notFoundDesc')}</p>
          </div>
          <button
            onClick={() => navigate('/contact')}
            className="px-4 py-2 rounded-md text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 transition-colors shrink-0"
          >
            {t('qna.contactCta')}
          </button>
        </div>
      </main>
    </div>
  )
}
